import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { withLoadingReducer } from 'utils/reducerHandler'
import { toastFailed, toastSuccess } from 'utils/toast'
import type {
  MassUploadResponseType,
  MassUploadTemplateResponseType,
} from 'utils/apiList/massUpload'

interface BulkUploadStateType {
  isLoading: boolean
  downloadTemplateURL: string
  downloadErrorURL: string
}

interface PostBulkUploadArgsType {
  uploadAPI: (payload: FormData) => Promise<{ data: MassUploadResponseType }>
  payload: FormData
  handleGoBack: () => void
}

export const fetchGetTemplateURL = createAsyncThunk(
  'bulkUpload/fetchGetTemplateURL',
  async (
    downloadAPI: () => Promise<{ data: MassUploadTemplateResponseType }>,
    { rejectWithValue },
  ) => {
    try {
      const response = await downloadAPI()
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

export const fetchPostBulkUpload = createAsyncThunk(
  'bulkUpload/fetchPostBulkUpload',
  async ({ uploadAPI, payload, handleGoBack }: PostBulkUploadArgsType, { rejectWithValue }) => {
    try {
      const response = await uploadAPI(payload)
      const { data, error } = response.data

      if (data.status === 'success') {
        handleGoBack()
        toastSuccess('Data berhasil diupload')
      } else {
        toastFailed(error?.message)
      }

      if (data?.urlDownload) {
        window.open(data.urlDownload, '_blank')
      }

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(undefined)
    }
  },
)

const initialState: BulkUploadStateType = {
  isLoading: false,
  downloadTemplateURL: '',
  downloadErrorURL: '',
}

const bulkUpload = createSlice({
  name: 'bulkUpload',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: withLoadingReducer(
    (builder) => {
      builder
        .addCase(
          fetchGetTemplateURL.fulfilled,
          (state, action: PayloadAction<MassUploadTemplateResponseType>) => {
            state.downloadTemplateURL = action.payload.data.url
          },
        )
        .addCase(
          fetchPostBulkUpload.fulfilled,
          (state, action: PayloadAction<MassUploadResponseType>) => {
            state.downloadErrorURL = action.payload.data?.urlDownload
          },
        )
    },
    [fetchPostBulkUpload],
  ),
})

export const { reset } = bulkUpload.actions
export default bulkUpload.reducer
